import type { ElementRef } from '@angular/core';
import {
  Component,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  HostListener,
  viewChild,
} from '@angular/core';
import { DOCUMENT, UpperCasePipe } from '@angular/common';
import { Router, RouterOutlet, RouterLink, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { AuthService } from '@core/auth/auth.service';
import { TranslationService, TranslatePipe } from '@core/i18n';
import { ToastComponent } from '@shared/ui/toast/toast.component';
import { ADMIN_ICONS } from '@shared/icons/admin-icons';
import type { Locale } from '@core/i18n';
import { AdminSidebarComponent } from './admin-sidebar/admin-sidebar.component';
import { buildAdminBreadcrumbs } from './admin-breadcrumbs.util';

@Component({
  selector: 'app-admin-shell',
  standalone: true,
  imports: [
    UpperCasePipe,
    RouterOutlet,
    RouterLink,
    TranslatePipe,
    ToastComponent,
    AdminSidebarComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './admin-shell.component.html',
  styles: `
    :host {
      display: block;
      height: 100vh;
    }
  `,
})
export class AdminShellComponent {
  readonly auth = inject(AuthService);
  readonly translationService = inject(TranslationService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  readonly icons = ADMIN_ICONS;
  readonly sidebarCollapsed = signal(false);
  readonly sidebarOpen = signal(false);

  readonly userInitials = computed(() => {
    const username = this.auth.username();
    return username ? username.substring(0, 2).toUpperCase() : '?';
  });

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      takeUntilDestroyed()
    ),
    { initialValue: this.router.url }
  );

  readonly breadcrumbs = computed(() => buildAdminBreadcrumbs(this.currentUrl()));

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.sidebarOpen()) {
      this.closeSidebar();
      return;
    }

    if ((event.key === '/' || (event.metaKey && event.key === 'k')) && !this.isInputFocused()) {
      event.preventDefault();
      this.searchInput()?.nativeElement?.focus();
      return;
    }

    if (event.key === '[' && !this.isInputFocused()) this.sidebarCollapsed.set(true);
    if (event.key === ']' && !this.isInputFocused()) this.sidebarCollapsed.set(false);
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update(value => !value);
  }

  toggleMobileSidebar(): void {
    this.sidebarOpen.update(value => !value);
  }

  closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  onNavItemClick(): void {
    if (this.sidebarOpen()) this.closeSidebar();
  }

  async onLogout(): Promise<void> {
    await this.auth.logout();
  }

  onGlobalSearch(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) return;
    const query = event.target.value.trim();
    if (!query) return;
    this.router.navigate(['/users'], { queryParams: { search: query } });
    event.target.blur();
  }

  switchLocale(locale: Locale): void {
    this.translationService.setLocale(locale);
  }

  private isInputFocused(): boolean {
    const element = this.document.activeElement;
    if (!(element instanceof HTMLElement)) return false;
    const tag = element.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || element.isContentEditable;
  }
}
